// NameCompatibilityCalculator.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const NameCompatibilityCalculator = () => {
  const [nameData, setNameData] = useState({
    name1: '',
    name2: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNameData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateCompatibility = () => {
    const { name1, name2 } = nameData;

    if (!name1 || !name2) {
      setError('Please enter both names');
      return;
    }

    // Mock compatibility calculation logic
    const compatibilityScore = Math.floor(Math.random() * 101); // Random score between 0 and 100

    const newResults = {
      compatibilityScore,
      calculationSteps: [
        'Step 1: Input Names',
        `Name 1: ${name1}`,
        `Name 2: ${name2}`,
        '\nStep 2: Calculate Compatibility',
        `Compatibility Score: ${compatibilityScore}%`
      ]
    };

    setResults(newResults);

    // Update chart data
    setChartData({
      bar: {
        labels: ['Compatibility Score'],
        datasets: [{
          label: 'Score',
          data: [compatibilityScore],
          backgroundColor: ['rgba(75, 192, 192, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)'],
          borderWidth: 1
        }]
      },
      pie: {
        labels: ['Compatible', 'Not Compatible'],
        datasets: [{
          data: [compatibilityScore, 100 - compatibilityScore],
          backgroundColor: ['rgba(54, 162, 235, 0.7)', 'rgba(255, 99, 132, 0.7)'],
          borderColor: ['rgba(54, 162, 235, 1)', 'rgba(255, 99, 132, 1)'],
          borderWidth: 1
        }]
      }
    });
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Name Compatibility Calculator | Assess Name Matches</title>
        <meta name="description" content="Assess the compatibility between two names using our Name Compatibility Calculator. Get instant results and visualizations." />
        <meta name="keywords" content="name compatibility calculator, name match, compatibility score" />
        <meta property="og:title" content="Name Compatibility Calculator | Assess Name Matches" />
        <meta property="og:description" content="Assess the compatibility between two names using our Name Compatibility Calculator. Get instant results and visualizations." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/name-compatibility-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">💖</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Name Compatibility Calculator</h1>
            <p className="text-lg text-blue-600 font-semibold">Assess the compatibility between two names</p>
          </div>
        </div>
      </div>

      <div className="calculator-description space-y-4 mb-12">
        <p className="text-gray-700">
          This calculator assesses the compatibility between two names using a mock calculation method.
          It provides a fun way to see how well two names might match.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name1">
            Name 1:
          </label>
          <input
            type="text"
            id="name1"
            name="name1"
            value={nameData.name1}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter first name"
          />
        </div>

        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name2">
            Name 2:
          </label>
          <input
            type="text"
            id="name2"
            name="name2"
            value={nameData.name2}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter second name"
          />
        </div>

        <button
          onClick={calculateCompatibility}
          className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Compatibility
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="text-xl font-semibold mb-4">Compatibility Score</h4>
                <div className="space-y-2">
                  <p className="flex justify-between">
                    <span>Compatibility Score</span>
                    <span className="font-medium">{results.compatibilityScore}%</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mt-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Bar Chart</h4>
                <div className="h-96">
                  <Bar
                    data={chartData.bar}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Score'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Compatibility'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Compatibility Score'
                        }
                      }
                    }}
                  />
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Pie Chart</h4>
                <div className="h-96">
                  <Pie
                    data={chartData.pie}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Compatibility Distribution'
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-12 bg-gray-50 p-6 rounded-xl">
              <h4 className="text-xl font-semibold mb-4">Calculation Steps</h4>
              <div className="space-y-4">
                {results.calculationSteps.map((step, index) => (
                  <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                    <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use Name Compatibility Calculator <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to use the Name Compatibility Calculator effectively:
          </p>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Names</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Enter the first name in the "Name 1" field.</li>
                <li>Enter the second name in the "Name 2" field.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Calculate Compatibility</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Click the "Calculate Compatibility" button.</li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: Review Results</h3>
              <ul className="list-disc pl-8 space-y-2">
                <li>Check the compatibility score.</li>
                <li>View the bar and pie charts for visual representation.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Full FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">What is the Name Compatibility Calculator?</h3>
              <p className="text-gray-700">
                The Name Compatibility Calculator is a fun tool that assesses the compatibility between two names using a mock calculation method.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">How does the calculator work?</h3>
              <p className="text-gray-700">
                The calculator generates a random compatibility score between 0 and 100 to simulate the compatibility between the two names.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Is the compatibility score accurate?</h3>
              <p className="text-gray-700">
                No, the compatibility score is randomly generated for demonstration purposes only. It is not based on any scientific or statistical analysis.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this tool for any names?</h3>
              <p className="text-gray-700">
                Yes, you can use this tool for any names. However, the results are for fun and should not be taken seriously.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Instant Results</h3>
              <p className="text-gray-700">
                Get immediate compatibility scores between any two names.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Visualization</h3>
              <p className="text-gray-700">
                Visualize the compatibility score with interactive bar and pie charts.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">User-Friendly Interface</h3>
              <p className="text-gray-700">
                Easy-to-use interface with clear instructions and intuitive design.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-blue-600 mb-4">Fun and Engaging</h3>
              <p className="text-gray-700">
                A fun way to see how well two names might match, perfect for entertainment.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NameCompatibilityCalculator;